// Submenú "Configuración" del módulo Control Bancario.
//   Parámetros            → formulario de fila única por empresa
//   Reglas de conciliación → CRUD de reglas de matching automático
//   Formatos de extracto   → CRUD de formatos de importación por banco
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCog, ListChecks, Loader2, Pencil, Plus, Save, Settings, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import {
  Banco, Campo, FormatoExtracto, MATCH_MONTO, MATCH_POR, Modal, Parametros as TParametros,
  ReglaConciliacion, Switch, TIPO_FORMATO, cb, fieldCls, label,
} from './shared';

// ============================================================ PARÁMETROS =====
export function ParametrosCB() {
  const qc = useQueryClient();
  const { data: rows = [], isFetching } = useQuery<TParametros[]>({
    queryKey: ['cb-parametros'],
    queryFn: () => cb.list<TParametros>('parametros', '?select=*&limit=1'),
  });
  const actual = rows[0];
  const [form, setForm] = useState<TParametros | null>(null);
  useEffect(() => { if (actual) setForm(actual); }, [actual]);
  const set = (patch: Partial<TParametros>) => setForm((f) => (f ? { ...f, ...patch } : f));

  const guardar = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('Sin datos');
      const { id, ...body } = form;
      return cb.update('parametros', id, body);
    },
    onSuccess: () => { toast.success('Parámetros guardados'); void qc.invalidateQueries({ queryKey: ['cb-parametros'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isFetching || !form) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Settings className="w-5 h-5" /> Parámetros del módulo</h1>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">General</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Campo label="Moneda base"><input className={fieldCls} maxLength={3} value={form.moneda_base} onChange={(e) => set({ moneda_base: e.target.value.toUpperCase() })} /></Campo>
            <Campo label="Decimales"><input type="number" className={fieldCls} value={form.decimales} onChange={(e) => set({ decimales: Number(e.target.value) })} /></Campo>
            <Campo label="Días de alerta de cheque"><input type="number" className={fieldCls} value={form.dias_alerta_cheque} onChange={(e) => set({ dias_alerta_cheque: Number(e.target.value) })} /></Campo>
          </div>
          <div className="flex flex-col gap-2 border-t pt-3">
            <Switch checked={form.permite_sobregiro} onChange={(v) => set({ permite_sobregiro: v })} label="Permitir sobregiro en cuentas" />
            <Switch checked={form.requiere_aprobacion_pagos} onChange={(v) => set({ requiere_aprobacion_pagos: v })} label="Requerir aprobación de órdenes de pago (la aprueba un usuario distinto de quien la creó)" />
            {form.requiere_aprobacion_pagos && (
              <div className="pl-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Campo label="Monto máximo sin aprobación">
                    <input type="number" step="0.01" min="0" className={`${fieldCls} w-40`}
                      value={form.monto_max_sin_aprobacion ?? 0}
                      onChange={(e) => set({ monto_max_sin_aprobacion: Number(e.target.value) })} />
                  </Campo>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pagos por este monto o menos se pagan directamente; por encima exigen aprobación de otro usuario.
                    Con <b>0</b>, todos los pagos requieren aprobación.
                  </p>
                </div>
                <div>
                  <Campo label="Nivel mínimo del aprobador">
                    <input type="number" min={1} max={9} className={`${fieldCls} w-24`}
                      value={form.nivel_min_aprobador ?? 5}
                      onChange={(e) => set({ nivel_min_aprobador: Number(e.target.value) })} />
                  </Campo>
                  <p className="text-xs text-muted-foreground mt-1">
                    Quien apruebe o rechace debe tener este nivel de acceso o más (además de ser distinto de quien la creó).
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Órdenes de pago</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <Campo label="Prefijo de numeración"><input className={fieldCls} value={form.prefijo_orden_pago} onChange={(e) => set({ prefijo_orden_pago: e.target.value })} /></Campo>
          <Campo label="Siguiente número"><input type="number" className={fieldCls} value={form.siguiente_orden_pago} onChange={(e) => set({ siguiente_orden_pago: Number(e.target.value) })} /></Campo>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Conciliación (valores por defecto)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <Campo label="Tolerancia de días"><input type="number" className={fieldCls} value={form.concil_tolerancia_dias} onChange={(e) => set({ concil_tolerancia_dias: Number(e.target.value) })} /></Campo>
          <Campo label="Tolerancia de monto"><input type="number" step="0.01" className={fieldCls} value={form.concil_tolerancia_monto} onChange={(e) => set({ concil_tolerancia_monto: Number(e.target.value) })} /></Campo>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}><Save className="w-4 h-4 mr-1" /> Guardar cambios</Button>
      </div>
    </div>
  );
}

// ================================================ REGLAS DE CONCILIACIÓN =====
const reglaVacia = { nombre: '', prioridad: 100, activo: true, match_monto: 'exacto', tolerancia_monto: 0, tolerancia_dias: 0, match_por: 'referencia', patron: '', descripcion: '' };

export function ReglasConciliacion() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<ReglaConciliacion[]>({
    queryKey: ['cb-reglas'],
    queryFn: () => cb.list<ReglaConciliacion>('reglas_conciliacion', '?select=*&deleted_at=is.null&order=prioridad,nombre'),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof reglaVacia>(reglaVacia);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof reglaVacia>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (r?: ReglaConciliacion) => {
    if (r) { setEditId(r.id); setForm({ nombre: r.nombre, prioridad: r.prioridad, activo: r.activo, match_monto: r.match_monto, tolerancia_monto: r.tolerancia_monto, tolerancia_dias: r.tolerancia_dias, match_por: r.match_por, patron: r.patron ?? '', descripcion: r.descripcion ?? '' }); }
    else { setEditId(null); setForm(reglaVacia); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      const body = { ...form, patron: form.patron || null, descripcion: form.descripcion || null, prioridad: Number(form.prioridad) || 0, tolerancia_monto: Number(form.tolerancia_monto) || 0, tolerancia_dias: Number(form.tolerancia_dias) || 0 };
      return editId ? cb.update('reglas_conciliacion', editId, body) : cb.create('reglas_conciliacion', body);
    },
    onSuccess: () => { toast.success('Regla guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['cb-reglas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => cb.remove('reglas_conciliacion', id),
    onSuccess: () => { toast.success('Regla eliminada'); void qc.invalidateQueries({ queryKey: ['cb-reglas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><ListChecks className="w-5 h-5" /> Reglas de conciliación</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
      </div>
      <p className="text-sm text-muted-foreground">Reglas de emparejamiento automático libro-banco vs. extracto, evaluadas por prioridad (menor primero).</p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Reglas ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Prioridad</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Empareja por</th><th className="py-2 pr-3">Monto</th><th className="py-2 pr-3">Tol. días</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin reglas</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{r.prioridad}</td>
                  <td className="py-2 pr-3">{r.nombre}</td>
                  <td className="py-2 pr-3">{label(MATCH_POR, r.match_por)}</td>
                  <td className="py-2 pr-3">{label(MATCH_MONTO, r.match_monto)}</td>
                  <td className="py-2 pr-3">{r.tolerancia_dias}</td>
                  <td className="py-2 pr-3">{r.activo ? 'Activa' : 'Inactiva'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(r)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${r.nombre}"?`, { peligro: true })) eliminar.mutate(r.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal wide title={editId ? 'Editar regla' : 'Nueva regla'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Nombre *" className="col-span-2"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
              <Campo label="Prioridad"><input type="number" className={fieldCls} value={form.prioridad} onChange={(e) => set({ prioridad: Number(e.target.value) })} /></Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Emparejar por">
                <select className={fieldCls} value={form.match_por} onChange={(e) => set({ match_por: e.target.value })}>
                  {MATCH_POR.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </Campo>
              <Campo label="Criterio de monto">
                <select className={fieldCls} value={form.match_monto} onChange={(e) => set({ match_monto: e.target.value })}>
                  {MATCH_MONTO.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Tolerancia de monto"><input type="number" step="0.01" className={fieldCls} value={form.tolerancia_monto} onChange={(e) => set({ tolerancia_monto: Number(e.target.value) })} disabled={form.match_monto !== 'tolerancia'} /></Campo>
              <Campo label="Tolerancia de días"><input type="number" className={fieldCls} value={form.tolerancia_dias} onChange={(e) => set({ tolerancia_dias: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Patrón (texto/regex en la glosa)"><input className={fieldCls} value={form.patron} onChange={(e) => set({ patron: e.target.value })} placeholder="opcional" /></Campo>
            <Campo label="Descripción"><textarea className={`${fieldCls} h-20`} value={form.descripcion} onChange={(e) => set({ descripcion: e.target.value })} /></Campo>
            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.activo} onChange={(v) => set({ activo: v })} label="Activa" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// =================================================== FORMATOS DE EXTRACTO =====
const formatoVacio = { nombre: '', banco_id: '', tipo: 'csv', delimitador: ',', tiene_encabezado: true, formato_fecha: 'YYYY-MM-DD', col_fecha: '', col_descripcion: '', col_referencia: '', col_debito: '', col_credito: '', col_saldo: '', activo: true };

export function FormatosExtracto() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: bancos = [] } = useQuery<Banco[]>({
    queryKey: ['cb-bancos'],
    queryFn: () => cb.list<Banco>('bancos', '?select=id,nombre&deleted_at=is.null&order=nombre'),
  });
  const { data: rows = [], isFetching } = useQuery<(FormatoExtracto & { banco?: { nombre: string } })[]>({
    queryKey: ['cb-formatos'],
    queryFn: () => cb.list('formatos_extracto', '?select=*,banco:banco_id(nombre)&deleted_at=is.null&order=nombre'),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof formatoVacio>(formatoVacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof formatoVacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (r?: FormatoExtracto) => {
    if (r) { setEditId(r.id); setForm({ nombre: r.nombre, banco_id: r.banco_id ?? '', tipo: r.tipo, delimitador: r.delimitador, tiene_encabezado: r.tiene_encabezado, formato_fecha: r.formato_fecha, col_fecha: r.col_fecha ?? '', col_descripcion: r.col_descripcion ?? '', col_referencia: r.col_referencia ?? '', col_debito: r.col_debito ?? '', col_credito: r.col_credito ?? '', col_saldo: r.col_saldo ?? '', activo: r.activo }); }
    else { setEditId(null); setForm(formatoVacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      const body = { ...form, banco_id: form.banco_id || null, col_fecha: form.col_fecha || null, col_descripcion: form.col_descripcion || null, col_referencia: form.col_referencia || null, col_debito: form.col_debito || null, col_credito: form.col_credito || null, col_saldo: form.col_saldo || null };
      return editId ? cb.update('formatos_extracto', editId, body) : cb.create('formatos_extracto', body);
    },
    onSuccess: () => { toast.success('Formato guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['cb-formatos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => cb.remove('formatos_extracto', id),
    onSuccess: () => { toast.success('Formato eliminado'); void qc.invalidateQueries({ queryKey: ['cb-formatos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><FileCog className="w-5 h-5" /> Formatos de extracto</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
      </div>
      <p className="text-sm text-muted-foreground">Definición de cómo leer los estados de cuenta de cada banco para la importación de movimientos.</p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Formatos ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Banco</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Encabezado</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin formatos</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{r.nombre}</td>
                  <td className="py-2 pr-3">{r.banco?.nombre ?? 'Genérico'}</td>
                  <td className="py-2 pr-3">{label(TIPO_FORMATO, r.tipo)}</td>
                  <td className="py-2 pr-3">{r.tiene_encabezado ? 'Sí' : 'No'}</td>
                  <td className="py-2 pr-3">{r.activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(r)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${r.nombre}"?`, { peligro: true })) eliminar.mutate(r.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal wide title={editId ? 'Editar formato' : 'Nuevo formato'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
              <Campo label="Banco">
                <select className={fieldCls} value={form.banco_id} onChange={(e) => set({ banco_id: e.target.value })}>
                  <option value="">Genérico</option>
                  {bancos.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              </Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Tipo">
                <select className={fieldCls} value={form.tipo} onChange={(e) => set({ tipo: e.target.value })}>
                  {TIPO_FORMATO.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </Campo>
              <Campo label="Delimitador"><input className={fieldCls} value={form.delimitador} onChange={(e) => set({ delimitador: e.target.value })} /></Campo>
              <Campo label="Formato de fecha"><input className={fieldCls} value={form.formato_fecha} onChange={(e) => set({ formato_fecha: e.target.value })} /></Campo>
            </div>
            <div className="border-t pt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Mapeo de columnas (nombre o índice en el archivo)</p>
              <div className="grid grid-cols-3 gap-3">
                <Campo label="Fecha"><input className={fieldCls} value={form.col_fecha} onChange={(e) => set({ col_fecha: e.target.value })} /></Campo>
                <Campo label="Descripción"><input className={fieldCls} value={form.col_descripcion} onChange={(e) => set({ col_descripcion: e.target.value })} /></Campo>
                <Campo label="Referencia"><input className={fieldCls} value={form.col_referencia} onChange={(e) => set({ col_referencia: e.target.value })} /></Campo>
                <Campo label="Débito"><input className={fieldCls} value={form.col_debito} onChange={(e) => set({ col_debito: e.target.value })} /></Campo>
                <Campo label="Crédito"><input className={fieldCls} value={form.col_credito} onChange={(e) => set({ col_credito: e.target.value })} /></Campo>
                <Campo label="Saldo"><input className={fieldCls} value={form.col_saldo} onChange={(e) => set({ col_saldo: e.target.value })} /></Campo>
              </div>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <div className="flex flex-col gap-2">
                <Switch checked={form.tiene_encabezado} onChange={(v) => set({ tiene_encabezado: v })} label="El archivo tiene fila de encabezado" />
                <Switch checked={form.activo} onChange={(v) => set({ activo: v })} label="Activo" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
